"use client";

import { useMotionValue, motion, useMotionTemplate } from "framer-motion"; // Changed from "motion/react"
import React, { MouseEvent as ReactMouseEvent, useState } from "react";
import { CanvasRevealEffect } from './canvas-reveal-effect'; // Path relative to aceternity folder
import { cn } from '../../utils/cn'; // Path relative to aceternity folder

export const CardSpotlight = ({
  children,
  radius = 350,
  color = "#262626", // Default spotlight color
  className,
  ...props
}: {
  radius?: number;
  color?: string;
  children: React.ReactNode;
} & React.HTMLAttributes<HTMLDivElement>) => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  function handleMouseMove({
    currentTarget,
    clientX,
    clientY,
  }: ReactMouseEvent<HTMLDivElement>) {
    if (currentTarget) { 
        let { left, top } = currentTarget.getBoundingClientRect();
        mouseX.set(clientX - left);
        mouseY.set(clientY - top);
    }
  }

  const [isHovering, setIsHovering] = useState(false);
  const handleMouseEnter = (event: ReactMouseEvent<HTMLDivElement>) => { // Accept event
    // Set mouse position on enter to start animation at the correct point
    const { currentTarget, clientX, clientY } = event;
    if (currentTarget) {
      let { left, top } = currentTarget.getBoundingClientRect();
      mouseX.set(clientX - left);
      mouseY.set(clientY - top);
    }
    setIsHovering(true); // Set hovering state AFTER updating mouse position
  };
  const handleMouseLeave = () => setIsHovering(false);
  return (
    <div
      className={cn(
        "group/spotlight p-6 rounded-md relative border border-neutral-800 bg-black dark:border-neutral-800", // Default styles from the provided code
        className // User-provided classNames will be merged
      )}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      <motion.div
        className="pointer-events-none absolute z-0 -inset-px rounded-md opacity-0 transition duration-300 group-hover/spotlight:opacity-100"
        style={{
          backgroundColor: color, // This is the spotlight color itself
          maskImage: useMotionTemplate`
            radial-gradient(
              ${radius}px circle at ${mouseX}px ${mouseY}px,
              white,
              transparent 80%
            )
          `,
        }}
      >
        {isHovering && (
          <CanvasRevealEffect
            animationSpeed={5}
            containerClassName="bg-transparent absolute inset-0 pointer-events-none" // Ensure this is transparent
            colors={[
              [59, 130, 246],
              [139, 92, 246],
            ]}
            dotSize={3}
            mousePosition={[mouseX.get(), mouseY.get()]} // Pass current mouse position
          />
        )}
      </motion.div>
      {children}
    </div>
  );
};